// Hideo - sounds and background music of the app themes, generated with Web Audio (no audio files). Ported from the
// user's ProCut sounds (suoni.js). Discreet: OFF by default for everyone (muted, music off), so nothing changes until
// the user turns them on (the speaker button, TopControls.vue / MobileBar.vue, or Settings › Appearance).
// Each family of themes has its own set (Fluent: wood and glass; Aero: rich bells with echo; Nostalgia Channel:
// bubbles and plastic clicks; Four Keys: electronic blips), the others a soft base set. Music: Nostalgia Channel and
// Four Keys have their own tune, every other theme a slow carpet of chords; Default has none.
// Settings on this device: creatorOS_sounds = {"muted","effects","music","musicOn"} (parseSoundSettings).
// Without AudioContext (tests, very old browsers) every call does nothing. Who plays what: src/stores/sounds.ts.
import { parseStyle, styleDef, type StyleDef } from './appThemes'

export const SOUNDS_KEY = 'creatorOS_sounds'

export interface SoundSettings {
  /** Everything silent (the speaker button) */
  muted: boolean
  /** Volume of the effects and of the music, 0 … 1 */
  effects: number
  music: number
  /** Background music on */
  musicOn: boolean
}
export const DEFAULT_SOUNDS: Readonly<SoundSettings> = { muted: true, effects: 0.6, music: 0.35, musicOn: false }

const vol = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : d)
/** A stored value made safe (anything unreadable = the defaults) */
export function parseSoundSettings(raw: unknown): SoundSettings {
  let o: Record<string, unknown> = {}
  if (typeof raw === 'string') { try { const p = JSON.parse(raw); if (p && typeof p === 'object' && !Array.isArray(p)) o = p } catch { /* the defaults */ } }
  return {
    muted: typeof o.muted === 'boolean' ? o.muted : DEFAULT_SOUNDS.muted,
    effects: vol(o.effects, DEFAULT_SOUNDS.effects),
    music: vol(o.music, DEFAULT_SOUNDS.music),
    musicOn: typeof o.musicOn === 'boolean' ? o.musicOn : DEFAULT_SOUNDS.musicOn
  }
}
export function readSoundSettings(): SoundSettings {
  try { return parseSoundSettings(localStorage.getItem(SOUNDS_KEY)) } catch { return { ...DEFAULT_SOUNDS } }
}
export function storeSoundSettings(s: SoundSettings): void {
  try { localStorage.setItem(SOUNDS_KEY, JSON.stringify(s)) } catch { /* storage full or blocked */ }
}

/** The names of the effects */
export type SoundName = 'hover' | 'click' | 'confirm' | 'back' | 'open' | 'tab' | 'ok' | 'error' | 'notify' | 'theme'

// ===================================================================== engine
const S: SoundSettings = { ...DEFAULT_SOUNDS }
/** The theme the sounds follow (its key; 'default' = Hideo's own look) */
let styleKey = 'default'
/** Music lowered (long operations) or silent (teleprompter) */
let ducked = false, silenced = false
let ctx: AudioContext | null = null
let out: GainNode, fx: GainNode, echo: GainNode, mus: GainNode
let musicLoop: ReturnType<typeof setInterval>[] | null = null, musicKind: string | null = null

type AudioCtor = new () => AudioContext
const audioCtor = (): AudioCtor | null => {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor }
  return w.AudioContext || w.webkitAudioContext || null
}
/** Web Audio exists here */
export const audioAvailable = (): boolean => !!audioCtor()

/** Creates the audio graph the first time (after a user gesture, or the browser keeps it suspended) */
function engine(): boolean {
  if (ctx) return true
  const C = audioCtor()
  if (!C) return false
  try { ctx = new C() } catch { return false }
  out = ctx.createGain(); out.connect(ctx.destination)
  fx = ctx.createGain(); fx.connect(out)
  mus = ctx.createGain(); mus.gain.value = 0; mus.connect(out)
  // Echo: a delay feeding back into itself, the "reverb" of the menus
  const delay = ctx.createDelay(1); delay.delayTime.value = 0.19
  const feedback = ctx.createGain(); feedback.gain.value = 0.32
  const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 3200
  echo = ctx.createGain(); echo.gain.value = 0.2
  echo.connect(delay); delay.connect(filter); filter.connect(feedback); feedback.connect(delay); filter.connect(fx)
  volumes()
  return true
}
function volumes(): void {
  if (!ctx) return
  const now = ctx.currentTime
  out.gain.setTargetAtTime(S.muted ? 0 : 1, now, 0.05)
  fx.gain.setTargetAtTime(S.effects * 0.5, now, 0.05)
  const m = S.musicOn && !silenced ? S.music * 0.22 * (ducked ? 0.3 : 1) : 0
  mus.gain.setTargetAtTime(m, now, ducked ? 0.6 : 0.9)
}
const resume = () => { if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => { /* not allowed yet */ }) }

/** The theme whose sounds and music play */
export function setSoundStyle(key: string): void { styleKey = key; syncMusic() }
/** New settings (from the sounds store) */
export function setSoundSettings(s: SoundSettings): void { Object.assign(S, s); syncMusic() }
/** Music lowered while a long operation runs (an online save) */
export function duckMusic(on: boolean): void { ducked = on; volumes() }
/** Music silent (the teleprompter is open: the user is reading aloud) */
export function silenceMusic(on: boolean): void { silenced = on; volumes() }
/** A user gesture: the audio can start (browsers block it before), and the music if it is on */
export function unlockAudio(): void {
  if (S.muted || (!S.musicOn && !S.effects)) return
  if (!engine()) return
  resume()
  syncMusic()
}

// The timbre of the theme: waveform, how much echo, how long the tails, how bright
function timbre(s: StyleDef | undefined): { wave: OscillatorType; echo: number; tail: number; high: number } {
  if (s?.family === 'aero') return { wave: 'sine', echo: 0.34, tail: 1.5, high: 1.5 }
  if (s?.family === 'fluent') return { wave: 'triangle', echo: 0.06, tail: 0.55, high: 1 }
  if (s?.family === 'bubbles') return { wave: 'sine', echo: 0.2, tail: 1, high: 1 }
  return { wave: 'triangle', echo: 0.12, tail: 0.8, high: 1 }
}
/** A note: quick attack, fading tail; part of it goes to the echo */
function note(freq: number, after: number, dur: number, v: number, wave?: OscillatorType): void {
  const c = ctx!, T = timbre(styleDef(styleKey)), t0 = c.currentTime + after, d = dur * T.tail
  const o = c.createOscillator(), gn = c.createGain()
  o.type = wave || T.wave; o.frequency.value = freq * T.high
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.006); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + d)
  o.connect(gn); gn.connect(fx)
  const s = c.createGain(); s.gain.value = T.echo; gn.connect(s); s.connect(echo)
  o.start(t0); o.stop(t0 + d + 0.05)
}
/** A breath: filtered noise rising or falling (dialogs, theme change) */
function breath(dur: number, v: number, from: number, to: number): void {
  const c = ctx!, t0 = c.currentTime, n = Math.floor(c.sampleRate * dur)
  const buf = c.createBuffer(1, n, c.sampleRate), x = buf.getChannelData(0)
  for (let i = 0; i < n; i++) x[i] = Math.random() * 2 - 1
  const src = c.createBufferSource(), f = c.createBiquadFilter(), gn = c.createGain()
  src.buffer = buf; f.type = 'bandpass'; f.Q.value = 1.2
  f.frequency.setValueAtTime(from, t0); f.frequency.exponentialRampToValueAtTime(to, t0 + dur)
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + dur * 0.35); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  src.connect(f); f.connect(gn); gn.connect(fx); src.start(t0)
}
/** A voice made of partials ([multiple, level]) */
function voice(freq: number, after: number, dur: number, v: number, partials: number[][], attack: number, echoAmount: number): void {
  const c = ctx!, t0 = c.currentTime + after, gn = c.createGain(), s = c.createGain()
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + attack); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  s.gain.value = echoAmount; gn.connect(fx); gn.connect(s); s.connect(echo)
  for (const [k, a] of partials) {
    const o = c.createOscillator(), ga = c.createGain()
    o.type = 'sine'; o.frequency.value = freq * k; ga.gain.value = a
    o.connect(ga); ga.connect(gn); o.start(t0); o.stop(t0 + dur + 0.05)
  }
}
const marimba = (f: number, d: number, dur: number, v: number) => voice(f, d, dur, v, [[1, 1], [4, 0.12]], 0.002, 0.05)
const glass = (f: number, d: number, dur: number, v: number) => voice(f, d, dur, v, [[1, 1], [2.76, 0.18]], 0.003, 0.12)
const bell = (f: number, d: number, dur: number, v: number) => voice(f, d, dur, v, [[1, 1], [2, 0.35], [3, 0.15], [4.2, 0.08]], 0.004, 0.55)
const tick = (f: number, d: number, v: number) => voice(f, d, 0.02, v, [[1, 1]], 0.001, 0)
/** A bubble popping: a sine rising fast */
function bubble(from: number, to: number, after: number, dur: number, v: number): void {
  const c = ctx!, t0 = c.currentTime + after, o = c.createOscillator(), gn = c.createGain()
  o.type = 'sine'; o.frequency.setValueAtTime(from, t0); o.frequency.exponentialRampToValueAtTime(to, t0 + dur * 0.6)
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.004); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  const s = c.createGain(); s.gain.value = 0.12
  o.connect(gn); gn.connect(fx); gn.connect(s); s.connect(echo); o.start(t0); o.stop(t0 + dur + 0.05)
}
/** A short dry click, like a plastic key */
function plastic(freq: number, after: number, v: number): void {
  const c = ctx!, t0 = c.currentTime + after, o = c.createOscillator(), gn = c.createGain(), f = c.createBiquadFilter()
  o.type = 'square'; o.frequency.value = freq; f.type = 'bandpass'; f.frequency.value = freq * 1.5; f.Q.value = 6
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.002); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.03)
  o.connect(f); f.connect(gn); gn.connect(fx); o.start(t0); o.stop(t0 + 0.05)
}
/** An electronic blip with a filter opening (Four Keys) */
function blip(freq: number, after: number, dur: number, v: number, rise?: number): void {
  const c = ctx!, t0 = c.currentTime + after, o = c.createOscillator(), o2 = c.createOscillator(), gn = c.createGain(), f = c.createBiquadFilter()
  o.type = 'sawtooth'; o2.type = 'sine'; o.frequency.value = freq; o2.frequency.value = freq / 2
  if (rise) { o.frequency.exponentialRampToValueAtTime(freq * rise, t0 + dur * 0.7); o2.frequency.exponentialRampToValueAtTime(freq * rise / 2, t0 + dur * 0.7) }
  f.type = 'lowpass'; f.Q.value = 7; f.frequency.setValueAtTime(freq * 0.8, t0); f.frequency.exponentialRampToValueAtTime(freq * 4, t0 + dur * 0.5)
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.005); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  const s = c.createGain(); s.gain.value = 0.25
  o.connect(f); o2.connect(f); f.connect(gn); gn.connect(fx); gn.connect(s); s.connect(echo)
  o.start(t0); o2.start(t0); o.stop(t0 + dur + 0.05); o2.stop(t0 + dur + 0.05)
}
/** A deep soft thump: a sine falling fast */
function thump(freq: number, after: number, v: number): void {
  const c = ctx!, t0 = c.currentTime + after, o = c.createOscillator(), gn = c.createGain()
  o.type = 'sine'; o.frequency.setValueAtTime(freq * 2, t0); o.frequency.exponentialRampToValueAtTime(freq, t0 + 0.08)
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.004); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25)
  o.connect(gn); gn.connect(fx); o.start(t0); o.stop(t0 + 0.3)
}

type Pack = Record<SoundName, () => void>
const BASE: Pack = {
  hover: () => note(2093, 0, 0.035, 0.035),
  click: () => note(1047, 0, 0.09, 0.12),
  confirm: () => { note(784, 0, 0.12, 0.12); note(1175, 0.07, 0.2, 0.11) },
  back: () => { note(988, 0, 0.1, 0.1); note(659, 0.06, 0.16, 0.09) },
  open: () => breath(0.22, 0.05, 600, 2400),
  tab: () => { note(1568, 0, 0.04, 0.06); note(1568, 0.045, 0.05, 0.05) },
  ok: () => { note(1047, 0, 0.5, 0.08); note(1319, 0.06, 0.5, 0.07); note(1568, 0.12, 0.7, 0.07) },
  error: () => { note(220, 0, 0.28, 0.14, 'triangle'); note(196, 0.09, 0.32, 0.12, 'triangle') },
  notify: () => { note(1319, 0, 0.6, 0.09, 'sine'); note(1976, 0.11, 0.9, 0.07, 'sine') },
  theme: () => breath(0.8, 0.06, 300, 3000)
}
const NOSTALGIA: Pack = {
  hover: () => plastic(2600, 0, 0.05),
  click: () => bubble(500, 1100, 0, 0.11, 0.16),
  confirm: () => { bubble(600, 1250, 0, 0.1, 0.14); bubble(900, 1900, 0.08, 0.16, 0.12) },
  back: () => { bubble(1100, 700, 0, 0.1, 0.12); bubble(800, 450, 0.07, 0.14, 0.1) },
  open: () => { bubble(400, 900, 0, 0.12, 0.1); plastic(3000, 0.1, 0.03) },
  tab: () => { plastic(2200, 0, 0.06); plastic(2800, 0.05, 0.05) },
  ok: () => [1047, 1319, 1568, 2093].forEach((f, i) => note(f, i * 0.07, 0.45, 0.07, 'sine')),
  error: () => { bubble(500, 260, 0, 0.2, 0.14); bubble(380, 200, 0.12, 0.24, 0.12) },
  notify: () => { note(1568, 0, 0.5, 0.08, 'sine'); note(2093, 0.12, 0.8, 0.07, 'sine') },
  theme: () => [784, 988, 1175, 1568].forEach((f, i) => bubble(f * 0.8, f, i * 0.06, 0.15, 0.08))
}
const FLUENT: Pack = {
  hover: () => tick(3400, 0, 0.018),
  click: () => marimba(880, 0, 0.09, 0.13),
  confirm: () => { marimba(784, 0, 0.1, 0.12); marimba(1175, 0.06, 0.14, 0.1) },
  back: () => { marimba(1047, 0, 0.09, 0.1); marimba(784, 0.055, 0.12, 0.09) },
  open: () => breath(0.16, 0.035, 2200, 5200),
  tab: () => { tick(2600, 0, 0.03); tick(3100, 0.04, 0.025) },
  ok: () => { glass(1319, 0, 0.35, 0.07); glass(1568, 0.07, 0.35, 0.065); glass(2093, 0.14, 0.6, 0.06) },
  error: () => { voice(311, 0, 0.22, 0.13, [[1, 1], [2, 0.1]], 0.004, 0.03); voice(233, 0.1, 0.28, 0.11, [[1, 1], [2, 0.1]], 0.004, 0.03) },
  notify: () => { glass(1568, 0, 0.4, 0.07); glass(2349, 0.1, 0.6, 0.06) },
  theme: () => [1047, 1319, 1568, 2093].forEach((f, i) => glass(f, i * 0.05, 0.3, 0.04))
}
const AERO: Pack = {
  hover: () => voice(1760, 0, 0.08, 0.025, [[1, 1], [2, 0.2]], 0.002, 0.3),
  click: () => { breath(0.04, 0.05, 2500, 3500); tick(1400, 0, 0.05) },
  confirm: () => { bell(1175, 0, 0.5, 0.08); bell(1760, 0.08, 0.7, 0.07) },
  back: () => { bell(1319, 0, 0.35, 0.06); bell(880, 0.07, 0.5, 0.055) },
  open: () => { breath(0.45, 0.05, 400, 2600); bell(2093, 0.2, 0.6, 0.025) },
  tab: () => bell(1568, 0, 0.25, 0.045),
  ok: () => [523.3, 659.3, 784, 1046.5, 1318.5].forEach((f, i) => bell(f, i * 0.09, i === 4 ? 1.6 : 0.7, 0.06)),
  error: () => [440, 554.4, 622.3].forEach(f => bell(f, 0, 0.9, 0.055)),
  notify: () => { bell(1319, 0, 0.6, 0.07); bell(1976, 0.14, 1, 0.06) },
  theme: () => { breath(1, 0.05, 250, 3500); [784, 988, 1175, 1568, 1976].forEach((f, i) => bell(f, 0.15 + i * 0.08, 0.8, 0.03)) }
}
const FOURKEYS: Pack = {
  hover: () => blip(1800, 0, 0.04, 0.025),
  click: () => { thump(180, 0, 0.16); blip(900, 0, 0.08, 0.05) },
  confirm: () => { thump(160, 0, 0.15); blip(660, 0, 0.14, 0.07, 1.5); blip(1320, 0.07, 0.18, 0.05) },
  back: () => { blip(990, 0, 0.12, 0.06, 0.66); thump(140, 0.04, 0.1) },
  open: () => { breath(0.35, 0.06, 300, 4000); blip(1320, 0.15, 0.2, 0.03) },
  tab: () => { blip(1400, 0, 0.05, 0.04); blip(1760, 0.05, 0.06, 0.035) },
  // The four keys in a row: four rising notes, one per colour
  ok: () => { thump(130, 0, 0.14); [784, 988, 1175, 1568].forEach((f, i) => blip(f, i * 0.08, i === 3 ? 0.6 : 0.22, 0.06)) },
  error: () => { blip(330, 0, 0.22, 0.1, 0.8); blip(247, 0.12, 0.3, 0.09, 0.8); thump(90, 0, 0.12) },
  notify: () => { blip(1175, 0, 0.3, 0.06); blip(1760, 0.1, 0.5, 0.05) },
  theme: () => { breath(0.9, 0.06, 200, 5000); thump(110, 0.1, 0.16); [523, 659, 784, 1047].forEach((f, i) => blip(f, 0.25 + i * 0.07, 0.3, 0.04)) }
}
/** The set of effects of a theme */
export function soundPack(key: string): 'base' | 'nostalgia' | 'fluent' | 'aero' | 'fourkeys' {
  const f = styleDef(parseStyle(key))?.family
  return f === 'fourkeys' || f === 'nostalgia' || f === 'fluent' || f === 'aero' ? f : 'base'
}
const PACKS = { base: BASE, nostalgia: NOSTALGIA, fluent: FLUENT, aero: AERO, fourkeys: FOURKEYS }

/** Plays an effect of the current theme (nothing when muted, at volume 0, or without Web Audio). Never throws */
export function playSound(name: SoundName): void {
  if (S.muted || !S.effects) return
  if (!engine()) return
  resume()
  try { PACKS[soundPack(styleKey)][name]() } catch { /* a sound must never stop the app */ }
}

// ===================================================================== music
/** The music of a theme (null = none: Default; an old stored key gives its new theme's) */
export function musicOf(key: string): StyleDef['music'] | null { return styleDef(parseStyle(key))?.music ?? null }

// Soft chords, with a high note now and then (every theme without its own tune)
const CHORDS = [[261.6, 329.6, 392, 493.9], [220, 277.2, 329.6, 415.3], [174.6, 220, 261.6, 329.6], [196, 246.9, 293.7, 370]]
const SPARKS = [1046.5, 1174.7, 1318.5, 1568, 1760, 2093]
function chord(i: number): void {
  const c = ctx!, t0 = c.currentTime, dur = 9
  const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 900; f.connect(mus)
  CHORDS[i % CHORDS.length].forEach((fr, k) => [-4, 4].forEach(st => {
    const o = c.createOscillator(), gn = c.createGain()
    o.type = 'sine'; o.frequency.value = fr / (k === 0 ? 2 : 1); o.detune.value = st
    gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(0.09, t0 + 3)
    gn.gain.setValueAtTime(0.09, t0 + dur - 3.5); gn.gain.linearRampToValueAtTime(0, t0 + dur + 1)
    o.connect(gn); gn.connect(f); o.start(t0); o.stop(t0 + dur + 1.2)
  }))
}
function spark(): void {
  const c = ctx!, t0 = c.currentTime, o = c.createOscillator(), gn = c.createGain(), s = c.createGain()
  o.type = 'sine'; o.frequency.value = SPARKS[Math.floor(Math.random() * SPARKS.length)]
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(0.05, t0 + 0.02); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.2)
  s.gain.value = 0.5; o.connect(gn); gn.connect(mus); gn.connect(s); s.connect(echo); o.start(t0); o.stop(t0 + 2.4)
}
function musicNote(wave: OscillatorType, freq: number, t0: number, dur: number, v: number): GainNode {
  const c = ctx!, o = c.createOscillator(), gn = c.createGain()
  o.type = wave; o.frequency.value = freq
  gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(v, t0 + 0.01); gn.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  o.connect(gn); gn.connect(mus); o.start(t0); o.stop(t0 + dur + 0.05)
  return gn
}
// Nostalgia Channel: a light menu tune, 100 beats a minute, four jazz chords (II-V-I-VI), soft bass on the strong beats,
// plucked chords on the off-beats, a bell inventing the melody (random notes of the scale, a motif repeated every two
// bars: no existing melody)
const LOOP_N: [number, number[]][] = [[146.8, [349.2, 440, 523.3]], [196, [349.2, 493.9, 587.3]], [130.8, [329.6, 392, 493.9]], [110, [329.6, 392, 523.3]]]
const SCALE = [523.3, 587.3, 659.3, 784, 880, 1046.5, 1174.7]
let barN = 0, motif: number[] = []
function barNostalgia(): void {
  const c = ctx!, b = 0.6, t0 = c.currentTime + 0.05, [bass, acc] = LOOP_N[barN % LOOP_N.length]
  musicNote('sine', bass, t0, b * 1.6, 0.16)
  musicNote('sine', bass * 1.5, t0 + 2 * b, b * 1.4, 0.12)
  ;[0.5, 1.5, 2.5, 3.5].forEach((x, k) => acc.forEach(f => musicNote('triangle', f, t0 + x * b + (k % 2) * 0.04, 0.22, 0.035)))
  if (barN % 2 === 0) motif = Array.from({ length: 5 }, () => SCALE[Math.floor(Math.random() * SCALE.length)])
  ;[0, 0.75, 1.5, 2.5, 3].forEach((x, k) => {
    if (Math.random() < 0.8) {
      const gn = musicNote('sine', motif[k], t0 + x * b, 0.55, 0.06)
      const s = c.createGain(); s.gain.value = 0.35; gn.connect(s); s.connect(echo)
      musicNote('sine', motif[k] * 2, t0 + x * b, 0.25, 0.015)   // the bell: an octave above, light
    }
  })
  barN++
}
// Four Keys: an electronic menu carpet, 96 beats a minute: pulsing bass in eighths, a soft pad of minor chords
// (i-VI-III-VII) and a filtered arpeggio invented every two bars
const LOOP_K: [number, number[]][] = [[110, [220, 261.6, 329.6]], [87.3, [174.6, 220, 261.6]], [130.8, [261.6, 329.6, 392]], [98, [196, 246.9, 293.7]]]
let barK = 0, arp: number[] = []
function barFourKeys(): void {
  const c = ctx!, b = 0.625, t0 = c.currentTime + 0.05, [bass, acc] = LOOP_K[barK % LOOP_K.length]
  for (let k = 0; k < 8; k++) musicNote('sine', bass, t0 + k * b / 2, 0.26, k % 2 ? 0.06 : 0.12)
  acc.forEach(f => [-6, 6].forEach(st => {
    const o = c.createOscillator(), gn = c.createGain(), fl = c.createBiquadFilter()
    o.type = 'sawtooth'; o.frequency.value = f; o.detune.value = st; fl.type = 'lowpass'; fl.frequency.value = 700
    gn.gain.setValueAtTime(0, t0); gn.gain.linearRampToValueAtTime(0.018, t0 + 0.8); gn.gain.linearRampToValueAtTime(0, t0 + 4 * b + 0.3)
    o.connect(fl); fl.connect(gn); gn.connect(mus); o.start(t0); o.stop(t0 + 4 * b + 0.4)
  }))
  if (barK % 2 === 0) arp = Array.from({ length: 8 }, () => acc[Math.floor(Math.random() * 3)] * (Math.random() < 0.5 ? 2 : 4))
  arp.forEach((f, k) => {
    if (Math.random() < 0.85) {
      const gn = musicNote('triangle', f, t0 + k * b / 2, 0.3, 0.03)
      const s = c.createGain(); s.gain.value = 0.4; gn.connect(s); s.connect(echo)
    }
  })
  barK++
}

function stopMusic(): void {
  if (musicLoop) musicLoop.forEach(clearInterval)
  musicLoop = null; musicKind = null
}
/** Starts, changes or stops the music to match the theme and the settings */
export function syncMusic(): void {
  const want = musicOf(styleKey)
  if (musicLoop && musicKind !== want) stopMusic()
  // Only once the audio exists (a user gesture created it: unlockAudio), never on its own at start-up
  if (want && S.musicOn && !S.muted && ctx) {
    resume()
    if (!musicLoop) {
      musicKind = want
      try {
        if (want === 'fourkeys') { barFourKeys(); musicLoop = [setInterval(barFourKeys, 2500)] }
        else if (want === 'nostalgia') { barNostalgia(); musicLoop = [setInterval(barNostalgia, 2400)] }
        else { let i = 0; chord(i++); musicLoop = [setInterval(() => chord(i++), 7500), setInterval(() => { if (Math.random() < 0.55) spark() }, 2300)] }
      } catch { stopMusic() }
    }
  } else if (musicLoop) stopMusic()
  volumes()
}

/** For tests: the engine's state */
export const soundState = () => ({ settings: { ...S }, styleKey, ducked, silenced, playing: musicKind, started: !!ctx })
